import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { Toaster } from "sonner";

import { msalInstance } from "./auth/msalConfig";
import { AuthProvider, useAuthContext } from "./auth/AuthContext";
import { CurrentUserProvider, useCurrentUser } from "./auth/CurrentUserContext";
import SessionGuard from "./auth/SessionGuard";
import { AuthBridgeProvider } from "./bridge/AuthBridgeContext";
import { isEmbeddedInExtension } from "./bridge/types";
import { Member360ApolloProvider } from "./lib/apollo/Member360ApolloProvider";
import { RoutePersister } from "./lib/RoutePersister";
import VersionWatcher from "./lib/VersionWatcher";
import { useAstranaPortalScope } from "./lib/useAstranaPortalScope";
import { useDropNavigationGuard } from "./lib/useDropNavigationGuard";
import { useGlobalErrorReporter } from "./lib/useGlobalErrorReporter";
import AppLayout from "./components/AppLayout";
import TabbedAppShell from "./components/TabbedAppShell";
import {
  HospitalEdGate,
  MemberSearchGate,
  PracticeScheduleGate,
  WorklistGate,
} from "./components/routeGates";
import { ErrorBoundary } from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner";
import LoginPage from "./pages/LoginPage";
import ExtSignInPopupPage from "./pages/ExtSignInPopupPage";
import { EXT_SIGNIN_PATH } from "./auth/extSignInHandoff";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import HomePage from "./home/HomePage";
import MemberSearchPage from "./members/MemberSearchPage";
import MemberProfilePage from "./members/MemberProfilePage";
import ProviderPortalDocumentsPage from "./members/Documents/ProviderPortalDocumentsPage";
import PracticeSchedulePage from "./schedule/PracticeSchedulePage";
import WorklistPage from "./worklist/WorklistPage";
import HospitalEdPage from "./hospitalEd/HospitalEdPage";
import AdminLayout from "./admin/components/AdminLayout";
import UsersPage from "./admin/pages/UsersPage";
import PersonasPage from "./admin/pages/PersonasPage";
import FeatureFlagsPage from "./admin/pages/FeatureFlagsPage";
import { featureFlags } from "./config/featureFlags";

// Dev-only: dialog rendering harness (no auth) for reproducing portal/CSS bugs.
const DialogHarness = import.meta.env.DEV
  ? React.lazy(() => import("./dev/DialogHarness"))
  : null;

const embedded = isEmbeddedInExtension();

/**
 * Route guard for authentication and optional RBAC checks.
 *
 * Props (all optional beyond `children`):
 *  - `requiredRole`       — gate on useCurrentUser().hasRole(value)
 *  - `requiredPermission` — gate on useCurrentUser().hasPermission(value)
 *  - `requiredPersona`    — gate on useCurrentUser().currentPersona === value
 *
 * Auth-only routes (no new props) work exactly as before.
 * While the RBAC query is loading → renders the existing loading state (no redirect).
 * Authenticated but fails RBAC check → renders an inline "not authorized" message.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
  requiredPersona?: string;
}

function ProtectedRoute({
  children,
  requiredRole,
  requiredPermission,
  requiredPersona,
}: ProtectedRouteProps) {
  const auth = useAuthContext();
  const currentUser = useCurrentUser();

  if (auth.isLoading) return <LoadingSpinner />;
  if (!auth.isAuthenticated) return <Navigate to="/login" replace />;

  // Only blank the screen on the INITIAL RBAC resolution (no role known yet).
  // A background refetch (e.g. a mutation that refetches connectCurrentUser, or a
  // persona switch) also flips `loading` true — but once we already have a resolved
  // role, returning the full-screen spinner here would unmount the whole route
  // subtree (incl. AdminLayout's nav + header), causing a jarring pop-out/in on
  // every admin action. Keep the chrome mounted and let the page body show its own
  // inline loading state instead.
  if (currentUser.loading && currentUser.role === null) return <LoadingSpinner />;

  // If connectCurrentUser errored AND this route carries any RBAC requirement,
  // block access. With errorPolicy:'all' the query can set error while user=null;
  // falling through to rbacFails would treat null persona as allow-all, contradicting
  // the CurrentUserContext docstring that says null → fail-closed for unknown state.
  // Unrestricted routes (no requiredRole/Permission/Persona) are not blocked so
  // the base clinical experience survives a transient RBAC query failure.
  const hasRbacRequirement =
    requiredRole !== undefined ||
    requiredPermission !== undefined ||
    requiredPersona !== undefined;

  if (currentUser.error && hasRbacRequirement) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-6">
        <div
          role="alert"
          className="max-w-sm w-full rounded-xl border border-destructive/40 bg-destructive/10 px-6 py-5 text-center"
        >
          <p className="text-base font-semibold text-destructive m-0 mb-1">Access Unavailable</p>
          <p className="text-sm text-muted-foreground m-0 mb-4">
            Unable to verify your permissions. This is usually an expired session — sign out and back in to fix it.
          </p>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => { void auth.logout(); }}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const rbacFails =
    (requiredRole !== undefined && !currentUser.hasRole(requiredRole)) ||
    (requiredPermission !== undefined && !currentUser.hasPermission(requiredPermission)) ||
    (requiredPersona !== undefined && currentUser.currentPersona !== requiredPersona);

  if (rbacFails) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-6">
        <div
          role="alert"
          className="max-w-sm w-full rounded-xl border border-destructive/40 bg-destructive/10 px-6 py-5 text-center"
        >
          <p className="text-base font-semibold text-destructive m-0 mb-1">Not Authorized</p>
          <p className="text-sm text-muted-foreground m-0">
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * §3.0 Apollo lift: Member360ApolloProvider is mounted ONCE here, wrapping
 * all routes, instead of per-route. This ensures a single Apollo client and
 * cache is shared across route transitions, and allows CurrentUserProvider
 * (which issues connectCurrentUser) to be mounted above the route tree.
 *
 * Member360ApolloProvider requires BrowserRouter (uses useNavigate), so it
 * lives inside BrowserRouter but outside Routes. CurrentUserProvider requires
 * the Apollo client, so it is nested inside Member360ApolloProvider.
 *
 * No impact on GraphQLClientGate — that gate is inside MemberProfilePage and
 * governs only the @astrana/ui internal GraphQLProvider subtree.
 *
 * Cache safety note: InMemoryCache has no TTL. The member-profile query is
 * keyed by :id (safe). The key={token} remount on MemberProfilePage continues
 * to work — it remounts the profile subtree, not the Apollo client.
 */
function AppRoutes() {
  // SessionGuard lives inside <BrowserRouter> so it can navigate to
  // /login on explicit sign-out, and inside <AuthProvider> (mounted
  // above by the App component) so it can read auth state.
  //
  // P6b/P6d — the keep-alive tab shell, gated behind
  // VITE_FEATURE_STATEFUL_TAB_SHELL. As of P6d it renders in BOTH standalone
  // web and the Chrome extension side panel (TabbedAppShell composes its
  // responsive/icon-condensed tab bar into the extension's own
  // EmbeddedControls strip in that mode — see TabbedAppShell.tsx).
  const useTabShell = featureFlags.statefulTabShell;
  return (
    <BrowserRouter>
      <RoutePersister />
      <Member360ApolloProvider>
        <CurrentUserProvider>
          <SessionGuard>
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/login" element={<LoginPage />} />
              {DialogHarness && (
                <Route
                  path="/__dialog-harness"
                  element={
                    <React.Suspense fallback={null}>
                      <DialogHarness />
                    </React.Suspense>
                  }
                />
              )}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              {useTabShell ? (
                /* P6b — stateful keep-alive multi-tab shell (standalone web
                   only). A SINGLE catch-all route so the shell element stays
                   MOUNTED across /members ↔ /members/:id ↔ /worklist (a
                   distinct <Route> per path would remount on every switch and
                   defeat keep-alive). More-specific routes above/below still
                   win by react-router's segment ranking. */
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <TabbedAppShell />
                    </ProtectedRoute>
                  }
                />
              ) : (
                <>
                  {/* Home — classic-mode equivalent of the shell's "home" tab.
                      No gate: auth-protected like every other page here, but
                      not flag- or persona-gated. */}
                  <Route
                    path="/home"
                    element={
                      <ProtectedRoute>
                        <AppLayout>
                          <HomePage />
                        </AppLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/members"
                    element={
                      <ProtectedRoute>
                        <AppLayout>
                          <MemberSearchGate>
                            <MemberSearchPage />
                          </MemberSearchGate>
                        </AppLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/members/:id"
                    element={
                      <ProtectedRoute>
                        <AppLayout>
                          <MemberProfilePage />
                        </AppLayout>
                      </ProtectedRoute>
                    }
                  />
                  {/* Practice Schedule (ACT-1239) — classic (non-shell) mode.
                      In shell mode the /* catch-all above renders
                      TabbedAppShell, which owns /schedule as a keep-alive tab;
                      this explicit route is the classic-mode equivalent,
                      parallel to /members and /worklist. Persona-gated by
                      PracticeScheduleGate (no VITE flag — a real shipping
                      surface). */}
                  <Route
                    path="/schedule"
                    element={
                      <ProtectedRoute>
                        <AppLayout>
                          <PracticeScheduleGate>
                            <PracticeSchedulePage />
                          </PracticeScheduleGate>
                        </AppLayout>
                      </ProtectedRoute>
                    }
                  />
                  {/* #242 retired the ppiDemo bypass — worklist route is gated
                      solely by the real patientPriorityIndex flag now. */}
                  {featureFlags.patientPriorityIndex ? (
                    <Route
                      path="/worklist"
                      element={
                        <ProtectedRoute>
                          <WorklistGate>
                            <AppLayout>
                              <WorklistPage />
                            </AppLayout>
                          </WorklistGate>
                        </ProtectedRoute>
                      }
                    />
                  ) : (
                    /* Flag off in classic (non-shell) mode: without this route,
                       a direct nav to /worklist has no matching <Route> and no
                       catch-all here, so it renders blank. Fall back to the
                       default landing page (Home) instead. */
                    <Route path="/worklist" element={<Navigate to="/home" replace />} />
                  )}
                  {/* Full-page Hospital/ED view — same underlying panel and
                      gate as /worklist, just a different worklistVariant
                      (see HospitalEdPage.tsx / HospitalEdGate). Reached from
                      Home's HospitalEdEventsWidget "view more" link and the
                      PrimaryNav "Hospital/ED" tab. */}
                  {featureFlags.patientPriorityIndex ? (
                    <Route
                      path="/hospital-ed"
                      element={
                        <ProtectedRoute>
                          <HospitalEdGate>
                            <AppLayout>
                              <HospitalEdPage />
                            </AppLayout>
                          </HospitalEdGate>
                        </ProtectedRoute>
                      }
                    />
                  ) : (
                    <Route path="/hospital-ed" element={<Navigate to="/home" replace />} />
                  )}
                </>
              )}
              <Route
                path="/pp-documents/:companyId/:mpi"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <ProviderPortalDocumentsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              {/* Admin console — care-management-gql CONFIG plane (member_360
                  schema, direct-Postgres). Admin ops use the app-wide
                  Member360ApolloProvider; no separate connect-gql client.
                  LEGACY: gated behind VITE_FEATURE_LEGACY_ADMIN_CONSOLE
                  (default on) during the migration to the standalone
                  admin-console app. Flip the flag off per-environment once
                  that app is live there. */}
              {featureFlags.legacyAdminConsole && (
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    index
                    element={<Navigate to="/admin/users" replace />}
                  />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="personas" element={<PersonasPage />} />
                  <Route path="feature-flags" element={<FeatureFlagsPage />} />
                </Route>
              )}
            </Routes>
            </ErrorBoundary>
          </SessionGuard>
        </CurrentUserProvider>
      </Member360ApolloProvider>
    </BrowserRouter>
  );
}

export default function App() {
  // Scope @astrana/ui's body-portaled dialogs/popovers without classing <body>
  // (which would leak the lib's typography onto app chrome).
  useAstranaPortalScope();

  // AM-5828 — app-wide guards, mounted once regardless of embedded/standalone:
  //  - useDropNavigationGuard: a file dropped outside a real dropzone must
  //    never navigate the whole SPA away (see hook doc for the failure mode).
  //  - useGlobalErrorReporter: last-resort console backstop so an uncaught
  //    error/rejection anywhere in the app can never be silently invisible.
  useDropNavigationGuard();
  useGlobalErrorReporter();

  // Side-panel sign-in popup (see auth/extSignInHandoff.ts): a TOP-LEVEL window
  // that must render without the router / MSAL / AuthProvider / SessionGuard /
  // Apollo tree so it can never boot the app, auth-redirect, or touch the
  // opener's session. `embedded` is false here (window === window.parent).
  if (!embedded && window.location.pathname === EXT_SIGNIN_PATH) {
    return <ExtSignInPopupPage />;
  }

  // Provider mount order matters: AuthProvider depends on MsalProvider (web)
  // or AuthBridgeProvider (extension iframe) being above it in the tree.
  if (embedded) {
    return (
      <AuthBridgeProvider>
        <AuthProvider>
          <Toaster richColors position="top-right" closeButton />
          <VersionWatcher />
          <AppRoutes />
        </AuthProvider>
      </AuthBridgeProvider>
    );
  }
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <VersionWatcher />
        <AppRoutes />
      </AuthProvider>
    </MsalProvider>
  );
}
